import { isSubscriptionEntitlementError } from '@/lib/subscription-entitlements';
import { projectApi, type ProjectApiRecord } from '@/lib/api/projects';
import type { ApiError } from '@/lib/api/types';

export type ProjectAccessResolution =
  | { status: 'allowed'; project: ProjectApiRecord }
  | { status: 'denied'; error?: ApiError }
  | { status: 'missing'; error?: ApiError }
  | { status: 'error'; error?: ApiError };

// A navigation that already completed a fresh server access check can enter
// the same project without showing a second route-level gate. The receipt is
// short lived, scoped to the signed-in user, and consumed by that navigation.
const PREPARED_ACCESS_TTL_MS = 20_000;
const preparedProjectAccess = new Map<string, { userId: string; expiresAt: number }>();

export async function prepareProjectAccess(projectId: string, userId: string): Promise<ProjectAccessResolution> {
  const resolution = await resolveProjectAccess(projectId, { forceRefresh: true });
  if (resolution.status === 'allowed' && userId) {
    preparedProjectAccess.set(projectId, { userId, expiresAt: Date.now() + PREPARED_ACCESS_TTL_MS });
  } else {
    preparedProjectAccess.delete(projectId);
  }
  return resolution;
}

export function hasPreparedProjectAccess(projectId: string, userId: string | null | undefined): boolean {
  const receipt = preparedProjectAccess.get(projectId);
  if (!receipt) return false;
  if (!userId || receipt.userId !== userId || receipt.expiresAt <= Date.now()) {
    preparedProjectAccess.delete(projectId);
    return false;
  }
  return true;
}

export function consumePreparedProjectAccess(projectId: string, userId: string | null | undefined): boolean {
  const prepared = hasPreparedProjectAccess(projectId, userId);
  preparedProjectAccess.delete(projectId);
  return prepared;
}

export function isForbiddenProjectError(error: unknown): boolean {
  // A plan restriction does not revoke access to the project itself.
  if (isSubscriptionEntitlementError(error)) return false;
  if (!(error && typeof error === 'object')) {
    return false;
  }
  const maybeError = error as {
    code?: unknown;
    action?: unknown;
    details?: { httpStatus?: unknown; status?: unknown } | null;
  };
  return maybeError.code === 'FORBIDDEN'
    || maybeError.code === 'HTTP_403'
    || maybeError.action === 'check_access'
    || maybeError.details?.httpStatus === 403
    || maybeError.details?.status === 403;
}

export function isNotFoundProjectError(error: unknown): boolean {
  if (!(error && typeof error === 'object')) {
    return false;
  }
  const maybeError = error as {
    code?: unknown;
    details?: { httpStatus?: unknown; status?: unknown } | null;
  };
  return maybeError.code === 'NOT_FOUND'
    || maybeError.code === 'HTTP_404'
    || maybeError.details?.httpStatus === 404
    || maybeError.details?.status === 404;
}

/**
 * 项目页面的权威准入检查。
 *
 * `forceRefresh` 用于页面首次进入和收到运行中 403 后的重新确认，避免短期
 * meta cache 把已经撤销的权限误判为仍然有效。
 */
export async function resolveProjectAccess(
  projectId: string,
  options: { forceRefresh?: boolean } = {},
): Promise<ProjectAccessResolution> {
  const response = await projectApi.getProjectMeta(projectId, options);
  if (response.success !== false && response.data) {
    return { status: 'allowed', project: response.data };
  }
  if (isForbiddenProjectError(response.error)) {
    return { status: 'denied', error: response.error };
  }
  if (isNotFoundProjectError(response.error)) {
    return { status: 'missing', error: response.error };
  }
  return { status: 'error', error: response.error };
}
